/** What a file is, as the chip shows it: one icon and label per type the BE admits. */
export enum AttachmentType {
  Pdf = 'pdf',
  Doc = 'doc',
  Image = 'image',
  Bpmn = 'bpmn',
  Other = 'other',
}

/**
 * One file on the composer, from the pick until the message that carries it is sent. Its bytes go
 * up as a temp file as soon as it is picked; the harness reads it only once the message is sent.
 * The composer cannot send until every file is attached.
 */
export type ComposerFile = {
  /** Local id: the chip's identity across state changes, never sent to the BE. */
  id: string
  file: File
  name: string
  size: number
  mime: string
  type: AttachmentType
  /** SHA-256 of the content: what a duplicate is matched on. */
  hash: string
  /** 0–100 while the bytes go up; null once they have landed. */
  progress: number | null
  /** The document-plugin temp file the bytes landed in. */
  tempFileId: string | null
  /** Why the file failed, as the chip shows it. */
  error: string | null
  /** Whether trying the upload again can help: the chip offers Retry beside Remove. */
  recoverable: boolean
}

/** What the uploader reports for one file, in order. */
export type ComposerFileUploadEvent =
  | { type: 'progress'; percent: number }
  | { type: 'uploaded'; tempFileId: string }
  | { type: 'failed'; error: string; recoverable: boolean }

/** Why a picked file never became a chip. */
export enum FileRefusalReason {
  UnsupportedType = 'unsupported_type',
  Empty = 'empty',
  TooLarge = 'too_large',
  TooMany = 'too_many',
  AlreadyAttached = 'already_attached',
  AlreadyInConversation = 'already_in_conversation',
  /** The browser could not read its bytes. */
  Unreadable = 'unreadable',
}

/** What the conversation already holds, as a pick is checked against it: the content of each
 * file this tab sent, with the stored entry its panel row is under (null until the read after its
 * turn names it), and how many files count against its limit — those it lists too, which a reload
 * leaves without a content hash. */
export type FilesInConversation = {
  hashes: ReadonlyMap<string, string | null>
  count: number
}

/** One line of the refused-before-upload notice. */
export type FileRefusal = {
  name: string
  reason: FileRefusalReason
  /** The reason in the maker's words, e.g. "24.3 MB, the maximum is 20 MB". */
  detail: string
  /** What Show points at: the composer chip this file duplicates, or the panel row of the sent
   * file it repeats. */
  duplicateOfId: string | null
}

/** The outcome of one pick: the files that become chips, and the ones the notice lists. */
export type FilePickResult = {
  admitted: readonly ComposerFile[]
  refusals: readonly FileRefusal[]
}

/** A long list folded to its first chips and a summary of the rest. */
export type FoldedFiles = {
  shown: readonly ComposerFile[]
  hidden: readonly ComposerFile[]
}

/** What the fold chip says about the hidden chips; `failed` is drawn apart, in the danger tint. */
export type FoldSummary = {
  label: string
  failed: string | null
  /** "Show all {N} files". */
  title: string
}

/** What a file chip draws, wherever the file is: on the composer, or under the message that sent
 * it. */
export type FileChipView = {
  /** The type's glyph and tint, or the failure's; a spinner stands in for it while `loading`. */
  glyph: string
  loading: boolean
  /** How far the bytes have got, 0–100; null while the harness reads the file, for as long as it
   * takes. */
  progress: number | null
  name: string
  tooltip: string
  meta: string
  failed: boolean
  /** A failure a second try may get past: the chip offers Retry. */
  retryable: boolean
  /** Taken out of the conversation: still read in its message, greyed through. */
  removed: boolean
  /** Its name opens it. */
  openable: boolean
}

/** The one mark a chip draws before its name. */
export enum ComposerFileMark {
  Uploading = 'uploading',
  Attached = 'attached',
  Failed = 'failed',
}
